import { useEffect, useRef, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '@/integrations/supabase/client';

interface SessionManagerOptions {
  /** Interval in milliseconds to check session validity */
  checkInterval?: number;
  /** Whether to validate session with server */
  validateWithServer?: boolean;
  /** Time in milliseconds before session expires */
  sessionTimeout?: number;
  /** User object - if null, session management is disabled */
  user: any;
  /** Logout function to call when session is invalid */
  logout: () => void;
}

export function useSessionManager(options: SessionManagerOptions) {
  const {
    checkInterval = 5 * 60 * 1000, // 5 minutes
    validateWithServer = true,
    sessionTimeout = 30 * 60 * 1000, // 30 minutes
    user,
    logout
  } = options;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Function to validate session with server
  const validateSession = useCallback(async (): Promise<boolean> => {
    if (!user || !isSupabaseConfigured()) return true;

    try {
      // Check if user still exists and is active
      const { data, error } = await supabase
        .from('admin_users')
        .select('id, is_active, last_login')
        .eq('id', user.id)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.log('Session validation failed: User not found or inactive');
        return false;
      }

      // Check if session has expired based on last login
      if (data.last_login) {
        const lastLogin = new Date(data.last_login);
        const now = new Date();
        const timeDiff = now.getTime() - lastLogin.getTime();
        
        if (timeDiff > sessionTimeout) {
          console.log('Session validation failed: Session expired');
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  }, [user, sessionTimeout]);

  // Function to update last activity
  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Function to check session validity
  const checkSession = useCallback(async () => {
    if (!user) return;

    // Check if user has been inactive for too long
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    if (timeSinceLastActivity > sessionTimeout) {
      console.log('Session expired due to inactivity');
      logout();
      return;
    }

    // Validate with server if enabled
    if (validateWithServer) {
      const isValid = await validateSession();
      if (!isValid) {
        console.log('Session invalidated by server');
        logout();
        return;
      }
    }
  }, [user, sessionTimeout, validateWithServer, validateSession, logout]);

  // Set up session monitoring
  useEffect(() => {
    if (!user) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Start session monitoring
    intervalRef.current = setInterval(checkSession, checkInterval);

    // Update activity on user interactions
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    const handleActivity = () => {
      updateLastActivity();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [user, checkInterval, checkSession, updateLastActivity]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    updateLastActivity,
    checkSession
  };
}
